import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";
import { API } from "../../context/AuthContext";
import { MASCOT_URL } from "../flux/Decor";

const display = "font-display";

export const PLANS = [
  { id: "weekly", price: "$4.99", per: "/week", note: "Flexible" },
  { id: "monthly", price: "$15", per: "/month", note: "Save 25%" },
  { id: "annual", price: "$120", per: "/year", note: "Best value — 2 months free" },
];

export function PlanPicker({ selected, onSelect }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {PLANS.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p.id)}
          data-testid={`plan-${p.id}`}
          className={`rounded-xl border p-3 text-center transition-colors duration-200 ${selected === p.id ? "border-[#f59e0b] bg-[#f59e0b]/10" : "border-white/10 bg-gray-950 hover:border-white/25"}`}
        >
          <div className={`${display} text-lg font-bold ${selected === p.id ? "text-[#f59e0b]" : "text-white"}`}>{p.price}<span className="text-[10px] text-gray-400">{p.per}</span></div>
          <div className="text-[9px] font-mono text-gray-400 mt-0.5 uppercase">{p.note}</div>
        </button>
      ))}
    </div>
  );
}

export function useCheckout() {
  const [loading, setLoading] = useState(false);
  const checkout = async (packageId, profile) => {
    setLoading(true);
    try {
      if (profile) await axios.put(`${API}/account/profile`, profile);
      const res = await axios.post(`${API}/billing/checkout`, { package_id: packageId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout. Try again.");
      setLoading(false);
    }
  };
  return { loading, checkout };
}

const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-100 focus:border-[#f59e0b]/50 focus:outline-none";

export default function Paywall() {
  const [plan, setPlan] = useState("monthly");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const { loading, checkout } = useCheckout();

  const go = () => {
    if (!fullName.trim()) {
      toast.error("Enter your full name to continue.");
      return;
    }
    checkout(plan, { full_name: fullName, phone_display: phone });
  };

  const price = PLANS.find((p) => p.id === plan);

  return (
    <div className="bg-gradient-to-b from-[#1a1230] to-[#131f3a] border border-[#f59e0b]/30 p-6 rounded-2xl shadow-2xl space-y-4" data-testid="paywall-panel">
      <div className="text-center space-y-2">
        <img src={MASCOT_URL} alt="Professor Flux" className="w-20 h-20 object-contain mx-auto animate-float" />
        <h3 className={`${display} font-bold text-lg text-white uppercase tracking-wide`}>Your 5-Day Trial Has Ended!</h3>
        <p className="text-xs text-gray-300 leading-relaxed max-w-sm mx-auto">
          Pick a pass to keep the capacitor charged. Your timeline stays safe either way — no card was needed for your trial.
        </p>
      </div>
      <PlanPicker selected={plan} onSelect={setPlan} />
      <div className="space-y-2">
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={100} placeholder="Full name *" className={inputCls} data-testid="paywall-name-input" />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} placeholder="Phone (optional)" className={inputCls} data-testid="paywall-phone-input" />
      </div>
      <button
        onClick={go}
        disabled={loading}
        data-testid="checkout-btn"
        className={`w-full bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-60 text-gray-950 ${display} font-bold py-3 px-4 rounded-xl text-xs uppercase tracking-wider transition-colors duration-200 active:scale-[0.98] shadow-lg shadow-[#f59e0b]/20 flex items-center justify-center gap-2`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        Recharge — {price.price}{price.per}
      </button>
      <p className="text-[9px] font-mono text-gray-500 text-center">Secure card payment via Stripe · unlimited ingestion, OCR, transcription & insights</p>
    </div>
  );
}
